/**
 * ADMİN PANELİ — yalnız yetkili hesaplar (kök: emrealabuga@gmail.com + kökün
 * yetkilendirdiği adminler). Kullanıcı yönetimi: listeleme, yeni kullanıcı, parola
 * sıfırlama, paket atama, abonelik iptal/aktif, admin yetkisi (yalnız kök), kişi
 * başı token (USD) kullanımı. Paket tanımları: 3 kademe (site limiti + AI bütçesi).
 * NOT: Parolalar geri okunamaz (scrypt hash) — yalnız SIFIRLANABİLİR.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Shield, UserPlus, KeyRound, Loader2, Check, Package, Radio } from "lucide-react";
import { ApiKeysPanel } from "@/components/api-keys-panel";

type Pkg = { id: number; name: string; siteLimit: number; tokenBudgetUsd: string; priceMonthly: string; description: string };
type AdminUser = { id: number; email: string; name: string; isAdmin: boolean; isRoot: boolean; packageId: number | null; subscriptionActive: boolean; approved: boolean; createdAt: string; domains: number; usageTotal: number; usage30: number };
type Overview = { isRoot: boolean; rootEmail: string; packages: Pkg[]; users: AdminUser[] };
type CevrimiciSatir = { id: number; name: string | null; email: string; sonGorulme: string; oturum: number; durum: "aktif" | "bugun" | "eski"; erisimSiteleri: string | null };

export default function Admin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", packageId: "" });
  const [creating, setCreating] = useState(false);
  const [pkgEdit, setPkgEdit] = useState<Record<number, Partial<Pkg>>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-overview"],
    retry: false,
    queryFn: async (): Promise<Overview> => {
      const r = await fetch("/api/admin/overview");
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Erişim yok");
      return r.json();
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-overview"] });

  // ÇEVRİMİÇİ KULLANICILAR — 30 sn'de bir kendini tazeler (oturumun last_seen_at damgası)
  const { data: cevrimici } = useQuery<{ kullanicilar: CevrimiciSatir[] }>({
    queryKey: ["admin-cevrimici"],
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () => {
      const r = await fetch("/api/admin/cevrimici");
      if (!r.ok) throw new Error("alınamadı");
      return r.json();
    },
  });

  const patchUser = async (id: number, patch: Record<string, unknown>, okMsg: string) => {
    const r = await fetch(`/api/admin/users/${id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) });
    const d = await r.json().catch(() => ({}));
    toast(r.ok ? { title: okMsg } : { title: "İşlem başarısız", description: d.error, variant: "destructive" });
    refresh();
  };

  // Başvuru reddet = hesabı tamamen sil (onay bekleyenler için)
  const rejectUser = async (u: AdminUser) => {
    if (!window.confirm(`${u.email} başvurusu REDDEDİLSİN mi? Hesap ve tüm verileri kalıcı silinir.`)) return;
    const r = await fetch(`/api/admin/users/${u.id}`, { method: "DELETE" });
    const d = await r.json().catch(() => ({}));
    toast(r.ok ? { title: "Başvuru reddedildi — hesap silindi" } : { title: "İşlem başarısız", description: d.error, variant: "destructive" });
    refresh();
  };

  const resetPassword = async (u: AdminUser) => {
    const pw = window.prompt(`${u.email} için YENİ parola (en az 6 karakter):`);
    if (!pw) return;
    await patchUser(u.id, { password: pw }, "Parola güncellendi (kullanıcının oturumları kapatıldı)");
  };

  const createUser = async () => {
    if (!newUser.email || newUser.password.length < 6) { toast({ title: "E-posta ve en az 6 karakterli parola gerekli", variant: "destructive" }); return; }
    setCreating(true);
    try {
      const r = await fetch("/api/admin/users", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...newUser, packageId: newUser.packageId ? parseInt(newUser.packageId) : undefined }),
      });
      const d = await r.json().catch(() => ({}));
      toast(r.ok ? { title: "Kullanıcı oluşturuldu" } : { title: "Oluşturulamadı", description: d.error, variant: "destructive" });
      if (r.ok) setNewUser({ email: "", name: "", password: "", packageId: "" });
      refresh();
    } finally { setCreating(false); }
  };

  const savePkg = async (p: Pkg) => {
    const patch = pkgEdit[p.id];
    if (!patch) return;
    const r = await fetch(`/api/admin/packages/${p.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) });
    toast(r.ok ? { title: `${p.name} güncellendi` } : { title: "Paket güncellenemedi", variant: "destructive" });
    setPkgEdit((e) => { const n = { ...e }; delete n[p.id]; return n; });
    refresh();
  };

  if (isLoading) return <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /> Yükleniyor…</div>;
  if (error || !data) return (
    <div className="p-10 max-w-lg mx-auto"><Card className="border-error-300 dark:border-error-500/40"><CardContent className="p-6 text-center text-sm" style={{ color: "#b42318" }}>
      <Shield className="w-8 h-8 mx-auto mb-2" /> {(error as Error)?.message ?? "Bu panele erişim yetkin yok."}
    </CardContent></Card></div>
  );

  const inputCls = "border rounded-lg px-2.5 py-1.5 text-[13px] bg-white text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500";
  const pkgName = (id: number | null) => data.packages.find((p) => p.id === id)?.name ?? "—";

  return (
    <div className="p-4 md:p-6 max-w-(--breakpoint-2xl) mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-2 text-sm text-warning-700 dark:text-warning-400"><Shield className="w-4 h-4" /> Yönetim</div>
        <h1 className="text-3xl font-bold mt-1">Admin Paneli</h1>
        <p className="text-muted-foreground mt-1">Kullanıcılar, paketler, abonelikler ve AI kullanımı. {data.isRoot ? "Kök admin yetkisindesin." : "Admin yetkisindesin (admin ekleme/çıkarma yalnız kök adminde)."}</p>
      </div>

      {/* ── Çevrimiçi kullanıcılar ── */}
      {cevrimici && (
        <div>
          <div className="text-sm font-bold mb-2 flex items-center gap-1.5">
            <Radio className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Kimler Çevrimiçi
            <span className="text-[11px] font-normal text-muted-foreground">— son 15 dk içinde istek atan "aktif" sayılır · 30 sn'de bir tazelenir</span>
          </div>
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b text-left text-muted-foreground text-[11px]">
                <th className="p-3">Durum</th><th className="p-3">Kullanıcı</th><th className="p-3">Son görülme</th><th className="p-3">Açık oturum</th><th className="p-3">Erişim verilen siteler</th>
              </tr></thead>
              <tbody>
                {cevrimici.kullanicilar.map((k) => (
                  <tr key={k.id} className="border-b last:border-0">
                    <td className="p-3">
                      {k.durum === "aktif"
                        ? <span className="inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#039855" }}><span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: "#12b76a" }} /><span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: "#12b76a" }} /></span> ÇEVRİMİÇİ</span>
                        : k.durum === "bugun"
                          ? <span className="text-[11px] font-semibold" style={{ color: "#b54708" }}>● bugün girdi</span>
                          : <span className="text-[11px] text-muted-foreground">○ çevrimdışı</span>}
                    </td>
                    <td className="p-3"><div className="font-medium">{k.name || "—"}</div><div className="text-[11px] text-muted-foreground">{k.email}</div></td>
                    <td className="p-3 tabular-nums">{new Date(k.sonGorulme).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="p-3 tabular-nums">{k.oturum}</td>
                    <td className="p-3 text-[12px] text-muted-foreground max-w-[280px] truncate" title={k.erisimSiteleri ?? ""}>{k.erisimSiteleri ? k.erisimSiteleri.replace(/https?:\/\/(www\.)?/g, "").replace(/\/(,|$)/g, "$1") : "kendi siteleri"}</td>
                  </tr>
                ))}
                {cevrimici.kullanicilar.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground text-[12px]">Açık oturum yok.</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </div>
      )}

      {/* ── Sistem API anahtarları + bakiyeler ── */}
      <ApiKeysPanel scope="system" />

      {/* ── Paketler ── */}
      <div>
        <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Package className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Paketler</div>
        <div className="grid md:grid-cols-3 gap-3">
          {data.packages.map((p) => {
            const e = pkgEdit[p.id] ?? {};
            const val = <K extends keyof Pkg>(k: K) => (e[k] ?? p[k]) as string | number;
            const set = (k: keyof Pkg, v: string | number) => setPkgEdit((s) => ({ ...s, [p.id]: { ...s[p.id], [k]: v } }));
            return (
              <Card key={p.id}><CardContent className="p-4 space-y-2">
                <input className={`${inputCls} w-full font-semibold`} value={val("name")} onChange={(ev) => set("name", ev.target.value)} />
                <label className="block text-[11px] text-muted-foreground">Site limiti
                  <input type="number" min={1} className={`${inputCls} w-full mt-0.5`} value={val("siteLimit")} onChange={(ev) => set("siteLimit", parseInt(ev.target.value) || 1)} /></label>
                <label className="block text-[11px] text-muted-foreground">Aylık AI bütçesi (USD)
                  <input type="number" min={0} className={`${inputCls} w-full mt-0.5`} value={val("tokenBudgetUsd")} onChange={(ev) => set("tokenBudgetUsd", ev.target.value)} /></label>
                <label className="block text-[11px] text-muted-foreground">Aylık fiyat
                  <input type="number" min={0} className={`${inputCls} w-full mt-0.5`} value={val("priceMonthly")} onChange={(ev) => set("priceMonthly", ev.target.value)} /></label>
                {pkgEdit[p.id] && (
                  <Button size="sm" className="bg-brand-500 hover:bg-brand-600 w-full gap-1" onClick={() => void savePkg(p)} >
                    <Check className="w-3.5 h-3.5" /> Kaydet
                  </Button>
                )}
              </CardContent></Card>
            );
          })}
        </div>
      </div>

      {/* ── Yeni kullanıcı ── */}
      <Card className="border-brand-200 dark:border-brand-500/30"><CardContent className="p-4">
        <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><UserPlus className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Yeni Kullanıcı Tanımla</div>
        <div className="flex flex-wrap gap-2 items-center">
          <input className={inputCls} placeholder="E-posta" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} style={{ width: 220 }} />
          <input className={inputCls} placeholder="İsim" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} style={{ width: 150 }} />
          <input className={inputCls} placeholder="Parola (min 6)" type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} style={{ width: 150 }} />
          <select className={inputCls} value={newUser.packageId} onChange={(e) => setNewUser({ ...newUser, packageId: e.target.value })}>
            <option value="">Paket yok (sınırsız)</option>
            {data.packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button size="sm" onClick={() => void createUser()} disabled={creating} className="bg-brand-500 hover:bg-brand-600 gap-1" >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Oluştur
          </Button>
        </div>
      </CardContent></Card>

      {/* Bekleyen başvuru uyarısı */}
      {data.users.some((u) => !u.approved) && (
        <div className="mb-4 rounded-xl border px-4 py-3 text-sm font-medium" style={{ borderColor: "#f7b267", background: "#fffaeb", color: "#b54708" }}>
          ⏳ {data.users.filter((u) => !u.approved).length} hesap başvurusu onay bekliyor — aşağıdaki listede "ONAY BEKLİYOR" rozetli satırlardan onayla ya da reddet.
        </div>
      )}
      {/* ── Kullanıcılar ── */}
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b text-left text-muted-foreground text-[11px]">
              <th className="p-3">Kullanıcı</th><th className="p-3">Paket</th><th className="p-3">Site</th>
              <th className="p-3">AI (30g)</th><th className="p-3">AI (toplam)</th><th className="p-3">Abonelik</th>
              <th className="p-3">Admin</th><th className="p-3">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const pkg = data.packages.find((p) => p.id === u.packageId);
              const budget = pkg ? Number(pkg.tokenBudgetUsd) : null;
              const over = budget != null && u.usage30 > budget;
              return (
                <tr key={u.id} className="border-b last:border-0 align-middle">
                  <td className="p-3">
                    <div className="font-medium">{u.email} {u.isRoot && <span className="bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400 text-[10px] px-1.5 py-0.5 rounded-full ml-1" >KÖK</span>}
                      {!u.approved && <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-1 font-bold" style={{ background: "#fef0c7", color: "#b54708" }}>ONAY BEKLİYOR</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{u.name || "—"} · kayıt {new Date(u.createdAt).toLocaleDateString("tr-TR")}</div>
                    {!u.approved && (
                      <div className="mt-1.5 flex gap-1.5">
                        <button onClick={() => void patchUser(u.id, { approved: true }, "Başvuru onaylandı — kullanıcı artık giriş yapabilir")}
                          className="text-[11px] px-2.5 py-1 rounded-full font-semibold" style={{ background: "#dcfce7", color: "#039855" }}>
                          ✓ Onayla
                        </button>
                        <button onClick={() => void rejectUser(u)}
                          className="text-[11px] px-2.5 py-1 rounded-full font-semibold" style={{ background: "#fee2e2", color: "#b42318" }}>
                          ✕ Reddet
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <select className={inputCls} value={u.packageId ?? ""} disabled={u.isRoot}
                      onChange={(e) => void patchUser(u.id, { packageId: e.target.value ? parseInt(e.target.value) : null }, "Paket atandı")}>
                      <option value="">Yok</option>
                      {data.packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td className="p-3">{u.domains}{pkg ? <span className="text-muted-foreground">/{pkg.siteLimit}</span> : ""}</td>
                  <td className="p-3 font-medium" style={over ? { color: "#d92d20" } : undefined}>${u.usage30.toFixed(2)}{budget != null && <span className="text-[10px] text-muted-foreground">/${budget}</span>}</td>
                  <td className="p-3">${u.usageTotal.toFixed(2)}</td>
                  <td className="p-3">
                    {u.isRoot ? <span className="text-[11px] text-muted-foreground">aktif</span> : (
                      <button onClick={() => void patchUser(u.id, { subscriptionActive: !u.subscriptionActive }, u.subscriptionActive ? "Abonelik iptal edildi (oturumları kapatıldı)" : "Abonelik aktifleştirildi")}
                        className="text-[11px] px-2 py-1 rounded-full font-medium"
                        style={u.subscriptionActive ? { background: "#dcfce7", color: "#039855" } : { background: "#fee2e2", color: "#b42318" }}>
                        {u.subscriptionActive ? "Aktif — iptal et" : "İPTAL — aktifleştir"}
                      </button>
                    )}
                  </td>
                  <td className="p-3">
                    {u.isRoot ? <span className="text-[11px] text-warning-700 dark:text-warning-400">✓ kök</span> : data.isRoot ? (
                      <button onClick={() => void patchUser(u.id, { isAdmin: !u.isAdmin }, u.isAdmin ? "Admin yetkisi alındı" : "Admin yapıldı")}
                        className={`text-[11px] px-2 py-1 rounded-full font-medium ${u.isAdmin ? "bg-[#fef3c7] text-[#b54708]" : "bg-[#f4f4f2] text-[#70706c] dark:bg-white/10 dark:text-gray-300"}`}>
                        {u.isAdmin ? "Admin ✓" : "Admin yap"}
                      </button>
                    ) : <span className="text-[11px] text-muted-foreground">{u.isAdmin ? "admin" : "—"}</span>}
                  </td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" className="gap-1 text-[11px] h-7" onClick={() => void resetPassword(u)}>
                      <KeyRound className="w-3 h-3" /> Parola Sıfırla
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent></Card>
      <p className="text-[11px] text-muted-foreground">Parolalar güvenlik gereği geri okunamaz (tek yönlü hash) — yalnız sıfırlanabilir. Parola sıfırlama ve abonelik iptali, kullanıcının açık oturumlarını anında kapatır. AI kullanımı: asistan + brief analizleri + denetimler + içerik motorlarının kayıtlı maliyet toplamıdır.</p>
    </div>
  );
}
